import React, { useRef, useState } from 'react'
import styled from 'styled-components'
import { Popover, PopoverProps } from '../Navbar/Popover'
import { Flex } from '../Flex'

import { MenuItem } from '../MenuItem'

const StyledPopoverBox = styled(Flex)<{ customBoxPadding: string; minWidth?: string }>`
  flex-direction: column;
  padding: ${({ customBoxPadding }) => customBoxPadding};
  border: ${({ theme }) => theme.colors.gray10};
  gap: 16px;
  width: 100%;
  border-radius: 8px;
  /*
   * A menu given a width does not wrap its rows. Without it a two-word entry
   * ("Terminal App") breaks onto a second line the moment its icon is added,
   * and the whole list shifts. The white-space rule inherits, so the nested
   * label picks it up; menus that pass no minWidth keep the old behaviour.
   */
  ${({ minWidth }) => (minWidth ? `min-width: ${minWidth}; white-space: nowrap;` : '')}
`

/**
 * A separator between groups of entries. Rendered ABOVE the item that carries
 * `divider: true`, so the config reads in the order the eye does.
 */
const MenuDivider = styled.div`
  height: 1px;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.gray8};
  /* The box's own 16px gap already spaces the rows; a divider only needs to
     sit between them without adding a second gap of its own. */
  margin: -2px 0;
`

export interface SingleMenuItem {
  label: string
  link?: string
  icon?: React.ReactNode
  /** Draw a separator ABOVE this entry — it starts a new group. */
  divider?: boolean
  endIcon?: React.ReactNode
  fixedIconColor?: string
  verificationRestricted?: boolean
  tokenRestricted?: boolean
  visibleOnMobile?: boolean
  userVerified?: boolean
  onClick?: () => void
  active?: boolean
  hoverableIcon?: boolean
  web3Login?: boolean
  disabled?: boolean
}
export interface PopoverMenuProps extends Omit<PopoverProps, 'open'> {
  menuItems: SingleMenuItem[]
  onItemClick: (item: SingleMenuItem) => void
  children: React.ReactNode
  customBoxPadding?: string
  disabled?: boolean
  /** Widen the menu and stop its rows wrapping. */
  minWidth?: string
}
const PopoverMenu: React.FunctionComponent<PopoverMenuProps> = (props) => {
  const {
    menuItems,
    onItemClick,
    children,
    customBoxPadding = '21px 25px',
    disabled,
    minWidth,
    ...popoverProps
  } = props
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const popoverOpen = Boolean(anchorEl)
  const handleClose = () => {
    if (typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur()
    }
    setAnchorEl(null)
  }
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) return
    setAnchorEl(event.currentTarget)
  }
  const buttonRef = useRef<Partial<HTMLDivElement>>(null)
  return (
    <>
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child as React.ReactElement<any>, { onClick: handleClick, ref: buttonRef })
        }
        return child
      })}
      <Popover
        anchorEl={anchorEl}
        open={popoverOpen}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        {...popoverProps}
      >
        <StyledPopoverBox customBoxPadding={customBoxPadding} minWidth={minWidth}>
          {menuItems?.map((item, index) => (
            <React.Fragment key={item.link ? `${item.label}-${item.link}` : `${item.label}-${index}`}>
              {item.divider && <MenuDivider />}
              <MenuItem
                label={item.label}
                startIcon={item.icon}
                endIcon={item.endIcon}
                hoverableIcon={item.hoverableIcon}
                onItemClick={() => {
                  handleClose()
                  onItemClick(item)
                }}
                disabled={item.disabled}
              />
            </React.Fragment>
          ))}
        </StyledPopoverBox>
      </Popover>
    </>
  )
}
export default PopoverMenu
